/**
 * GameObject - Entity container
 * Holds parts and provides event dispatch
 */

import type { IPart } from './types';
import { Events } from './types';

let nextId = 1;

/**
 * Position in the world
 */
export interface Position {
  x: number;
  y: number;
}

/**
 * GameObject - the core entity in the ECS
 * Contains parts and dispatches events to them
 */
export class GameObject {
  readonly id: string;
  readonly blueprint: string;
  private parts = new Map<string, IPart>();
  private _position: Position | null = null;

  /** String properties (from blueprint or instance) */
  properties = new Map<string, string>();

  /** Integer properties (from blueprint or instance) */
  intProperties = new Map<string, number>();

  constructor(blueprint: string) {
    this.id = `obj_${nextId++}`;
    this.blueprint = blueprint;
  }

  /**
   * Get the object's position
   */
  get position(): Position | null {
    return this._position;
  }

  /**
   * Set the object's position and fire position changed event
   */
  set position(pos: Position | null) {
    const oldPos = this._position;
    this._position = pos;
    if (pos !== oldPos) {
      this.fireEvent(Events.POSITION_CHANGED, { old: oldPos, new: pos });
    }
  }

  /**
   * Add a part to this object
   */
  addPart(part: IPart): void {
    part.attach(this);
    this.parts.set(part.name, part);
  }

  /**
   * Remove a part from this object
   */
  removePart(name: string): IPart | null {
    const part = this.parts.get(name);
    if (part) {
      part.detach();
      this.parts.delete(name);
    }
    return part ?? null;
  }

  /**
   * Get a part by name
   */
  getPart<T extends IPart>(name: string): T | null {
    return (this.parts.get(name) as T) ?? null;
  }

  /**
   * Check if this object has a part
   */
  hasPart(name: string): boolean {
    return this.parts.has(name);
  }

  /**
   * Get all parts
   */
  getAllParts(): IPart[] {
    return Array.from(this.parts.values());
  }

  /**
   * Fire an event to all parts
   * Parts are notified in insertion order
   * If a part returns false, propagation stops
   */
  fireEvent(event: string, data?: unknown): void {
    for (const part of this.parts.values()) {
      if (!part.onEvent(event, data)) {
        break;
      }
    }
  }

  /**
   * Get a string property
   */
  getProperty(name: string): string | undefined {
    return this.properties.get(name);
  }

  /**
   * Set a string property
   */
  setProperty(name: string, value: string): void {
    this.properties.set(name, value);
  }

  /**
   * Get an integer property
   */
  getIntProperty(name: string): number | undefined {
    return this.intProperties.get(name);
  }

  /**
   * Set an integer property
   */
  setIntProperty(name: string, value: number): void {
    this.intProperties.set(name, value);
  }
}
